/**
 * PhotoUploadStatusBar - aggregate read-out for background photo uploads.
 *
 * Inside a workflow the photos aren't in one grid the user is looking at —
 * they're scattered across checklist rows the tech has already scrolled past.
 * A per-thumbnail chip can't answer the question that actually matters there:
 * "is everything I shot on the server yet?" This bar answers it in one line,
 * pinned where the user can see it while they keep working.
 *
 * It renders nothing when the queue is empty, so it costs no space until
 * there's something to say. Three states:
 *   - in flight  → count + spinner
 *   - waiting    → offline; the queue retries itself on reconnect
 *   - failed     → count + a Retry all action, because a failure the user
 *                  can't act on is just an accusation
 *
 * Pair it with `usePhotoUploadQueue`: pass `queue.items` straight in.
 *
 * @example
 * <PhotoUploadStatusBar items={photoUploads.items} onRetryAll={handleRetryAll} />
 */
import React from 'react';
import type { PendingPhotoUpload } from '../../../hooks/usePhotoUploadQueue';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface PhotoUploadStatusBarProps {
    /** Queue entries, straight from usePhotoUploadQueue. */
    items: PendingPhotoUpload<unknown>[];
    /** Retry every failed or waiting entry. Omit to hide the action. */
    onRetryAll?: () => void;
    /** Optional className appended to the root element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const PhotoUploadStatusBar: React.FC<PhotoUploadStatusBarProps> = ({ items, onRetryAll, className }) => {
    const uploading = items.filter((item) => item.status === 'uploading').length;
    const waiting = items.filter((item) => item.status === 'waiting').length;
    const failed = items.filter((item) => item.status === 'failed').length;

    /* Nothing pending — take up no room at all. */
    if (uploading + waiting + failed === 0) return null;

    /* Failures outrank everything else: they're the only state that needs
       the user to do something. */
    if (failed > 0) {
        return (
            <div className={['photo-upload-status-bar photo-upload-status-bar--failed flex items-center justify-between gap-8px px-16px py-8px bg-red-50 border-b border-red-200', className ?? ''].join(' ')} data-ref="molecule-photo-upload-status-bar" role="status">
                <span className="text-12px tablet:text-13px font-sans font-medium text-red-700 min-w-0 truncate">
                    {failed} photo{failed === 1 ? '' : 's'} failed to upload
                </span>
                {onRetryAll && (
                    <button type="button" onClick={onRetryAll} className="text-12px tablet:text-13px font-sans font-semibold text-red-700 underline underline-offset-2 hover:text-red-800 min-h-44px flex-shrink-0 px-4px" >
                        Retry all
                    </button>
                )}
            </div>
        );
    }

    if (waiting > 0) {
        return (
            <div className={['photo-upload-status-bar photo-upload-status-bar--waiting flex items-center gap-8px px-16px py-8px bg-amber-50 border-b border-amber-200', className ?? ''].join(' ')} data-ref="molecule-photo-upload-status-bar" role="status">
                <span className="text-12px tablet:text-13px font-sans font-medium text-amber-800">
                    {waiting} photo{waiting === 1 ? '' : 's'} saved on this device — they will upload when you are back on a signal
                </span>
            </div>
        );
    }

    return (
        <div className={['photo-upload-status-bar photo-upload-status-bar--uploading flex items-center gap-8px px-16px py-8px bg-brand-primary/5 border-b border-brand-border', className ?? ''].join(' ')} data-ref="molecule-photo-upload-status-bar" role="status">
            <svg className="animate-spin h-14px w-14px text-brand-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-12px tablet:text-13px font-sans font-medium text-brand-text-muted">
                Uploading {uploading} photo{uploading === 1 ? '' : 's'} in the background…
            </span>
        </div>
    );
};

export default PhotoUploadStatusBar;
